import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import {
  Dialog, Box, TextField, Button, Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useGlobal from 'global-state/store';
import { fillingStatusFromValue, formattedFillingStatusBasedOnRead } from 'components/utils/fillingType';
import { LoadingButton } from '@mui/lab';
import { getMappingConfig } from '../admin/inventories/mappingUtils';
import useFillingChange from './hooks/useFillingChange';

/**
 * @param {{
 *   open: boolean, setOpen: any, inventoryData: any, itemData: any, setInventoryData: any, formik?: any,
 * }} props
 */
export default function ChangeFillingDialog({
  open, setOpen, inventoryData, itemData, setInventoryData, formik: operationFormik,
}) {
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();

  // Get unified mapping configuration (handles both old and new formats)
  const mappingConfig = getMappingConfig(inventoryData);

  const defaultStatus = itemData[mappingConfig.columns.filling.mapping.csvName];
  const [options, setOptions] = useState([]);
  const { requestFillingChange, loading, dialog } = useFillingChange({
    inventoryData,
    itemData,
    setInventoryData,
    formik: operationFormik,
  });

  useEffect(() => {
    const newOptions = [
      formattedFillingStatusBasedOnRead(defaultStatus, 'draining'),
      formattedFillingStatusBasedOnRead(defaultStatus, 'empty'),
      formattedFillingStatusBasedOnRead(defaultStatus, 'full'),
    ].filter(Boolean);
    setOptions(newOptions);
  }, [defaultStatus]);

  const handleClose = () => {
    setOpen(false);
  };

  // Closes once the change is applied; cancelling the measurements question keeps it open.
  const handleSubmit = (newStatus) => requestFillingChange(newStatus, handleClose);

  const formik = useFormik({
    initialValues: {
      status: defaultStatus,
    },
    onSubmit: (values) => {
      if (fillingStatusFromValue(values.status) !== null) {
        handleSubmit(values.status);
      } else {
        globalActions.setSnackbarMessage({ message: t('invalid_format'), severity: 'error' });
      }
    },
  });

  const handleChange = (event, newValue) => {
    formik.setFieldValue('status', newValue);
  };

  return (
    <Dialog open={open} onClose={() => {}} fullWidth>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          my: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Autocomplete
          freeSolo
          disableClearable
          sx={{ width: '100%' }}
          value={formik.values.status}
          onChange={handleChange}
          inputValue={formik.values.status}
          onInputChange={(event, newInputValue) => {
            formik.setFieldValue('status', newInputValue);
          }}
          options={options}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              sx={{ width: '100%' }}
              label={t('operation.modify_filling')}
              name="status"
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
            />
          )}
        />
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%',
        }}
        >
          <Button color="error" onClick={handleClose}>{t('cancel')}</Button>
          <LoadingButton loading={loading} type="submit">{t('done')}</LoadingButton>
        </Box>

      </Box>
      {dialog}
    </Dialog>
  );
}
